import { useMemo } from 'react'

import type { ListHolidaysParams } from '@/utils/api/leave-service/models'

import { useListHolidays } from '@/utils/api/leave-service'

interface UseCurrentYearHolidaysOptions {
  page?: number
  size?: number
  enabled?: boolean
}

/**
 * Custom hook that fetches holidays and transforms their dates to the current year.
 * Automatically sets the date range from January 1 to December 31 of the current year
 * and updates all returned holiday dates to use the current year.
 */
export function useCurrentYearHolidays(options: UseCurrentYearHolidaysOptions = {}) {
  const { page = 0, size = 200, enabled = true } = options

  const currentYear = useMemo(() => new Date().getFullYear(), [])

  const holidaysParams = useMemo<ListHolidaysParams>(() => ({
    pageable: { page, size },
  }), [page, size])

  const query = useListHolidays(holidaysParams, {
    query: { enabled },
  })

  const transformedData = useMemo(() => {
    if (!query.data?.data?.content) {
      return query.data
    }

    const transformedContent = query.data.data.content.map((holiday) => {
      if (!holiday.holidayDate) {
        return holiday
      }

      // Extract month and day from the original date (YYYY-MM-DD format)
      const dateParts = holiday.holidayDate.split('-')
      if (dateParts.length === 3) {
        const [_, month, day] = dateParts
        return {
          ...holiday,
          holidayDate: `${currentYear}-${month}-${day}`,
        }
      }

      return holiday
    })

    return {
      ...query.data,
      data: {
        ...query.data.data,
        content: transformedContent,
      },
    }
  }, [query.data, currentYear])

  return {
    ...query,
    data: transformedData,
  }
}
